import { createFileRoute } from "@tanstack/react-router";
import { RouteError, RouteNotFound } from "@/components/route-error";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Plane, Plus, Trash2, MapPin } from "lucide-react";
import { listTrips, deleteTrip } from "@/lib/travel.functions";
import { getPeople } from "@/lib/relationships.functions";
import { PageHeader } from "@/components/page-header";
import { Avatar } from "@/components/avatar";
import { TripSheet } from "@/components/travel/trip-sheet";
import { TripDrafts } from "@/components/travel/trip-drafts";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/travel")({
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound, component: Travel });

function Travel() {
  const fetchTrips = useServerFn(listTrips);
  const fetchPeople = useServerFn(getPeople);
  const del = useServerFn(deleteTrip);
  const qc = useQueryClient();
  const { data: trips, isLoading } = useQuery({ queryKey: ["trips"], queryFn: () => fetchTrips() });
  const { data: people } = useQuery({ queryKey: ["people"], queryFn: () => fetchPeople() });

  const [open, setOpen] = useState(false);
  const [draftsForTrip, setDraftsForTrip] = useState<any | null>(null);

  const delMut = useMutation({
    mutationFn: (tripId: string) => del({ data: { tripId } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["trips"] }); toast.success("Trip removed"); },
  });

  const list = trips ?? [];
  const upcoming = list.filter((t: any) => t.when !== "past");
  const past = list.filter((t: any) => t.when === "past");
  const knownLocations = (people?.contacts ?? []).map((c: any) => c.location).filter(Boolean);

  return (
    <div className="space-y-6 pb-4">
      <PageHeader
        title="Travel"
        subtitle="Where you're heading. Who's there."
        action={
          <button onClick={() => setOpen(true)} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-3 text-sm font-medium text-primary-foreground">
            <Plus className="h-4 w-4" /> New trip
          </button>
        }
      />

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!isLoading && list.length === 0 && (
        <div className="glass rounded-3xl p-12 text-center">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary/15 text-primary">
            <Plane className="h-5 w-5" />
          </div>
          <h3 className="mt-4 text-base font-medium">Plan a trip</h3>
          <p className="mt-1 text-sm text-muted-foreground max-w-xs mx-auto">
            Tell Pyng where you're heading. We'll surface contacts in town with a draft "want to catch up?" note.
          </p>
          <button onClick={() => setOpen(true)} className="mt-4 inline-flex h-10 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground">
            <Plus className="h-4 w-4" /> New trip
          </button>
        </div>
      )}

      {upcoming.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-[15px] font-semibold text-foreground/90">Upcoming</h2>
          {upcoming.map((t: any) => (
            <TripCard key={t.id} trip={t} onOpen={() => setDraftsForTrip(t)} onDelete={() => delMut.mutate(t.id)} />
          ))}
        </section>
      )}

      {past.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-[15px] font-semibold text-foreground/90">Past</h2>
          {past.map((t: any) => (
            <TripCard key={t.id} trip={t} onOpen={() => setDraftsForTrip(t)} onDelete={() => delMut.mutate(t.id)} muted />
          ))}
        </section>
      )}

      <TripSheet open={open} onOpenChange={setOpen} knownLocations={knownLocations} />
      <TripDrafts trip={draftsForTrip} onClose={() => setDraftsForTrip(null)} />
    </div>
  );
}

function fmtRange(starts?: string | null, ends?: string | null) {
  const fmt = (d: string) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (!starts && !ends) return "Anytime";
  if (starts && ends && starts !== ends) return `${fmt(starts)} – ${fmt(ends)}`;
  return fmt((starts || ends)!);
}

function TripCard({ trip, onOpen, onDelete, muted }: { trip: any; onOpen: () => void; onDelete: () => void; muted?: boolean }) {
  const tag = trip.when === "active" ? "Active" : trip.when === "upcoming" ? "Upcoming" : "Past";
  return (
    <article className={`glass rounded-3xl p-5 ${muted ? "opacity-70" : ""}`}>
      <div className="flex items-start gap-4">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary/15 text-primary">
          <Plane className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-medium truncate">{trip.city}</h3>
            <span className="text-[12px] uppercase tracking-widest text-primary/80">{tag}</span>
          </div>
          <p className="text-[14px] text-muted-foreground">
            {fmtRange(trip.starts_on, trip.ends_on)}
            {" · "}
            {trip.contact_count === 0
              ? "no one in town or nearby"
              : [
                  (trip.in_town_count ?? trip.contact_count) > 0
                    ? `${trip.in_town_count ?? trip.contact_count} in town`
                    : null,
                  (trip.nearby_count ?? 0) > 0 ? `${trip.nearby_count} nearby` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
          </p>
          {trip.contacts.length > 0 && (
            <div className="mt-3 flex -space-x-2">
              {trip.contacts.map((c: any) => (
                <span key={c.id} className="ring-2 ring-background rounded-full">
                  <Avatar name={c.name} initials={c.initials} photoUrl={c.photo_url} size={26} />
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={onDelete}
          aria-label="Remove trip"
          className="grid h-8 w-8 place-items-center rounded-full glass-soft text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {trip.contact_count > 0 && trip.when !== "past" && (
        <button
          onClick={onOpen}
          className="mt-4 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-full bg-primary text-sm font-medium text-primary-foreground"
        >
          Draft "in town" notes
        </button>
      )}
      {trip.contact_count === 0 && (
        <p className="mt-3 text-[13px] text-muted-foreground inline-flex items-center gap-1">
          <MapPin className="h-3 w-3" /> No contacts within ~60km yet. Add their location on the contact page.
        </p>
      )}
    </article>
  );
}